import TrebbleCard from "../../../../common/TrebbleCard";
import TrebbleSwitch from "../../../../common/TrebbleSwitch";
import TrebbleButton from "../../../../common/TrebbleButton";
import TrebbleSkeleton from "../../../../common/TrebbleSkeleton";
import { Template } from "../../VideoEditor";
import {SavedCaptionDesignSystemConfig} from '../../../remotion/Components/Captions/Caption';
import { SquareBorderIcon, SmoothBorderIcon, RoundedBorderIcon } from './../FrameConfig/BorderRadiusIcons';
import { FieldContainer } from './FieldContainer';
import { useEffect, useState, useRef } from 'react';
import { CaptionEditForm } from './CaptionEditForm';
import { ArrowLeftOutlined } from "@ant-design/icons";
import { CaptionTemplate } from './CaptionTemplate';
import TrebbleClientAPIHelper from "../../../../../models/helper/TrebbleClientAPI";
import { CaptionList } from "./CaptionList";
import ti18n from 'i18n!nls/ClipCreatori18n';
import useDefaultTrebbleAntDTokenConfig from "../../../../helper/useDefaultTrebbleAntDTokenConfig";

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
  }

export const BORDER_CORNER_RADIUS = Object.freeze({
    SQUARE: {
        value: 0,
        label: window.getI18n(ti18n, "BORDER_RADIUS_SQUARE"),
        icon: <SquareBorderIcon />
    },
    SMOOTH: {
        value: 0.10,
        label: window.getI18n(ti18n, "BORDER_RADIUS_SMOOTH"),
        icon: <SmoothBorderIcon />
    },
    ROUNDED: {
        value: 0.30,
        label: window.getI18n(ti18n, "BORDER_RADIUS_ROUNDED"),
        icon: <RoundedBorderIcon />
    },
});

const CAPTION_CASES = Object.freeze({
    STANDARD: {
        value: "standard",
        label: window.getI18n(ti18n, "CASE_STANDARD"),
    },
    UPPERCASE: {
        value: "uppercase",
        label: window.getI18n(ti18n, "CASE_UPPERCASE"),
    },
});

// Preview text configurations
const PREVIEW_TEXTS = {
    one_word: [window.getI18n(ti18n, "PREVIEW_WORD")],
    one_line: [window.getI18n(ti18n, "PREVIEW_ONE_LINE")],
    two_lines: [window.getI18n(ti18n, "PREVIEW_TWO_LINES_1"), window.getI18n(ti18n, "PREVIEW_LINE_2")],
    three_lines: [window.getI18n(ti18n, "PREVIEW_TWO_LINES_1"), window.getI18n(ti18n, "PREVIEW_LINE_2"), window.getI18n(ti18n, "PREVIEW_THREE_LINES_3")]
} as const;

const PREVIEW_DURATION = 3000; // 3 seconds in milliseconds

// Control whether to show the selected caption preview in the fixed header
const SHOW_SELECTED_CAPTION_PREVIEW = true;

const SHOW_SELECTED_CAPTION_ONLY_FOR_SELECTED_THEME = true

// Add interface for the API response caption format
interface ApiCaption {
    id: string;
    name: string;
    category?: string;
    type: 'user' | 'trebble';
    captionConfig: {
        milisecondGrouping: number;
        font: string;
        useDesignSystemColor: boolean;
        mainColor: string;
        highlightColor: string;
        fontSizeRatio: "small" | "medium" | "large";
        outline: "none" | "glow" | "shadow" | "3d-shadow" | "stroke" | "3d-stroke";
        background: "none" | "white" | "colored" | "translucent";
        textAnimation: "none" | "pop" | "highlight" | "boxed" | "scale" | "karaoke" | "reveal" | "reveal_up" | "reveal_scale" | "reveal_box" | "karaoke_scale" | "karaoke_box";
        borderRadius: number;
        captionCase: "standard" | "uppercase";
        hidePunctuation: boolean;
        position: "top left" | "top center" | "top right" | "bottom left" | "bottom center" | "bottom right";
        maxLines: "one_line" | "two_lines" | "three_lines";
        showOneWordOnly: boolean;
    };
}

// Replace simulated API calls with actual API calls
const fetchUserCaptions = async (workspaceId: string | null | undefined) => {
    const api = TrebbleClientAPIHelper.getInstance();
    const captions = await api.getCaptions({ type: 'user' }, workspaceId);
    return captions.map((caption: ApiCaption) => ({
        ...caption
    }));
};

const fetchTrebbleCaptions = async (workspaceId: string | null | undefined) => {
    const api = TrebbleClientAPIHelper.getInstance();
    const captions = await api.getCaptions({ type: 'trebble' }, workspaceId);
    return captions.map((caption: ApiCaption) => ({
        ...caption
    
    }));
};

const saveCaption = async (caption: SavedCaptionDesignSystemConfig, workspaceId: string | null | undefined) => {
    const api = TrebbleClientAPIHelper.getInstance();
    if (caption.id) {
        return api.updateCaption(caption.id, caption, workspaceId);
    } else {
        return api.createCaption(caption, workspaceId);
    }
};

interface CaptionConfigProps {
    template: Template;
    onTemplateChange: (template: Template) => void;
    onEditingChange: (isEditing: boolean) => void;
    workspaceId: string | null | undefined;
}

export const CaptionConfig = ({ 
    template,
    onTemplateChange,
    onEditingChange,
    workspaceId
}: CaptionConfigProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingCaption, setEditingCaption] = useState<SavedCaptionDesignSystemConfig | null>(null);
    const [userCaptions, setUserCaptions] = useState<SavedCaptionDesignSystemConfig[]>([]);
    const [trebbleCaptions, setTrebbleCaptions] = useState<SavedCaptionDesignSystemConfig[]>([]);
    const [trebbleCaptionsByCategory, setTrebbleCaptionsByCategory] = useState<{ [key: string]: SavedCaptionDesignSystemConfig[] }>({});
    const [isLoading, setIsLoading] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [originalCaptionBeforeEdit, setOriginalCaptionBeforeEdit] = useState<SavedCaptionDesignSystemConfig | undefined | null>(null);
    const [originalOverrideThemeCaptionBeforeEdit, setOriginalOverrideThemeCaptionBeforeEdit] = useState<boolean | null>(null);
    const token = useDefaultTrebbleAntDTokenConfig();

    // Refs to hold the latest values for the cleanup function
    const isEditingRef = useRef(isEditing);
    const originalCaptionRef = useRef(originalCaptionBeforeEdit);
    const originalOverrideRef = useRef(originalOverrideThemeCaptionBeforeEdit);
    const templateRef = useRef(template);
    const onTemplateChangeRef = useRef(onTemplateChange);

    // Effect to keep refs updated with the latest values
    useEffect(() => {
        isEditingRef.current = isEditing;
        originalCaptionRef.current = originalCaptionBeforeEdit;
        originalOverrideRef.current = originalOverrideThemeCaptionBeforeEdit;
        templateRef.current = template;
        onTemplateChangeRef.current = onTemplateChange;
    }, [isEditing, originalCaptionBeforeEdit, originalOverrideThemeCaptionBeforeEdit, template, onTemplateChange]);

    // Derived selected caption based on override

    const getEffectiveSelectedCaptionConfig = (currentTemplate: Template)=>{
       return  currentTemplate.overrideThemeCaption
        ? currentTemplate.savedCaptionConfig
        : currentTemplate.savedThemeConfig.themeConfig?.savedCaptionConfig
    }
    const [selectedCaption, setSelectedCaption] = useState<SavedCaptionDesignSystemConfig | undefined>(getEffectiveSelectedCaptionConfig(template));

    useEffect(() => {
        setSelectedCaption(getEffectiveSelectedCaptionConfig(template));
    }, [template.overrideThemeCaption, template.savedThemeConfig.themeConfig?.savedCaptionConfig, template.savedCaptionConfig]);

    // Get the currently selected caption details from the loaded lists
    const selectedCaptionDetails = [...userCaptions, ...trebbleCaptions].find(t => 
        t.id === selectedCaption?.id
    );

    // Update the loadCaptions useEffect
    useEffect(() => {
        const loadCaptions = async () => {
            try {
                setIsLoading(true);
                const [userCaptionsData, trebbleCaptionsData] = await Promise.all([
                    fetchUserCaptions(workspaceId),
                    fetchTrebbleCaptions(workspaceId)
                ]);

                // Filter out any user captions from trebble captions
                const filteredTrebbleCaptions = trebbleCaptionsData.filter((caption: ApiCaption) => caption.type === 'trebble');
                
                // Filter out any trebble captions from user captions
                const filteredUserCaptions = userCaptionsData.filter((caption: ApiCaption) => caption.type === 'user');

                // Organize Trebble captions by category, with "Trebble Presets" as the main category
                const trebbleCaptionsByCategory = filteredTrebbleCaptions.reduce((acc: { [key: string]: SavedCaptionDesignSystemConfig[] }, caption: ApiCaption) => {
                    const category = caption.category || 'Trebble Presets';
                    if (!acc[category]) {
                        acc[category] = [];
                    }
                    acc[category].push(caption);
                    return acc;
                }, {});

                setUserCaptions(filteredUserCaptions);
                setTrebbleCaptions(filteredTrebbleCaptions);
                setTrebbleCaptionsByCategory(trebbleCaptionsByCategory);
                setIsLoading(false);
            } catch (error) {
                console.error("Error loading captions:", error);
                window.alertErrorMessage(error);
                setIsLoading(false);
            }
        };
        loadCaptions();
    }, [workspaceId]);

    const handleCaptionSelect = (savedCaption: SavedCaptionDesignSystemConfig) => {
        if (savedCaption.id === selectedCaption?.id) return;
        
        // Create a new template with the selected caption config
        const updatedTemplate = {
            ...template,
            savedCaptionConfig: savedCaption,
            overrideThemeCaption: true // Ensure theme override is turned off when selecting a caption
        };
        
        onTemplateChange(updatedTemplate);
    };

    const handleEdit = () => {
        if (!selectedCaptionDetails || selectedCaptionDetails.type === 'trebble') return;
        // Save scroll position before entering edit mode
        if (scrollContainerRef.current) {
            setScrollPosition(scrollContainerRef.current.scrollTop);
        }
        // Store original state
        setOriginalCaptionBeforeEdit(selectedCaption);
        setOriginalOverrideThemeCaptionBeforeEdit(template.overrideThemeCaption);
        setEditingCaption(selectedCaptionDetails);
        setIsEditing(true);
        onEditingChange(true);
    };

    const handleDuplicate = () => {
        if (!selectedCaptionDetails) return;

        const newId = Date.now().toString();
        const duplicatedCaption: SavedCaptionDesignSystemConfig = {
            ...selectedCaptionDetails,
            id: newId,
            type: 'user' as const,
            name: `${selectedCaptionDetails.name} Copy`,
            captionConfig: { 
                ...selectedCaptionDetails.captionConfig,
            }
        };

        // Store original state
        setOriginalCaptionBeforeEdit(selectedCaption);
        setOriginalOverrideThemeCaptionBeforeEdit(template.overrideThemeCaption);
        setEditingCaption(duplicatedCaption);
        setIsEditing(true);
        onEditingChange(true);
        // Update preview immediately
        onTemplateChange({
            ...template,
            savedCaptionConfig: duplicatedCaption,
            overrideThemeCaption: true
        });
        // Clear original state
        setOriginalCaptionBeforeEdit(null);
        setOriginalOverrideThemeCaptionBeforeEdit(null);
        onEditingChange(false);
    };

    const handleSave = async () => {
        if (!editingCaption) return;
        
        try {
            // Ensure the caption being saved is marked as 'user' type
            const captionToSave: SavedCaptionDesignSystemConfig = {
                ...editingCaption,
                type: 'user'
            };

            const savedCaption = await saveCaption(captionToSave, workspaceId);
            
            // Update local state: Add if new/duplicated, update if existing
            if (!userCaptions.find(c => c.id === savedCaption.id)) {
                // Add new/duplicated caption
                setUserCaptions(prev => [...prev, savedCaption]);
            } else {
                // Update existing caption
                setUserCaptions(prev => prev.map(caption => 
                    caption.id === savedCaption.id ? savedCaption : caption
                ));
            }
            
            // Set the saved caption as current and ensure override is true
            onTemplateChange({
                ...template,
                savedCaptionConfig: savedCaption,
                overrideThemeCaption: true // Saving explicitly selects this caption
            });
            setIsEditing(false);
            setEditingCaption(null);
            // Clear original state
            setOriginalCaptionBeforeEdit(null);
            setOriginalOverrideThemeCaptionBeforeEdit(null);
            onEditingChange(false);
        } catch (error) {
            console.error("Error saving caption:", error);
            window.alertErrorMessage(error);
        }
    };

    // Handle create new caption
    const handleCreateNew = () => {
        const newId = Date.now().toString();
        // Use a default structure for new captions
        const newCaption: SavedCaptionDesignSystemConfig = {
            id: newId,
            name: "New Caption",
            type: 'user' as const,
            captionConfig: { // Provide default config values
                milisecondGrouping: 1000,
                font: "Inter",
                useDesignSystemColor: true,
                mainColor: "#000000",
                highlightColor: "#ffffff",
                fontSizeRatio: "medium",
                outline: "none",
                background: "none",
                textAnimation: "none",
                borderRadius: 0.1,
                captionCase: "standard",
                hidePunctuation: false,
                position: "bottom center",
                maxLines: "two_lines",
                showOneWordOnly: false
            }
        };

        // Store original state
        setOriginalCaptionBeforeEdit(selectedCaption);
        setOriginalOverrideThemeCaptionBeforeEdit(template.overrideThemeCaption);
        setEditingCaption(newCaption);
        setIsEditing(true);
        onEditingChange(true);
        // Update preview immediately
        onTemplateChange({
            ...template,
            savedCaptionConfig: newCaption,
            overrideThemeCaption: true
        });
    };

    // Effect to handle reverting changes ONLY if the component unmounts while editing
    useEffect(() => {
        // This effect runs only on mount because of the empty dependency array
        return () => {
            // Cleanup function runs only on unmount
            // Access the latest values via refs
            if (isEditingRef.current && originalCaptionRef.current !== null && originalOverrideRef.current !== null) {
                console.log("CaptionConfig unmounting while editing, reverting caption...");
                onTemplateChangeRef.current({
                    ...templateRef.current, // Use template value from ref
                    savedCaptionConfig: originalCaptionRef.current,
                    overrideThemeCaption: originalOverrideRef.current
                });
                onEditingChange(false);
            }
        };
    }, []); // <-- Empty dependency array ensures cleanup runs only on unmount

    // Function to get theme caption message
    const getThemeCaptionMessage = () => {
        if (!template.savedThemeConfig.themeConfig) {
            return window.getI18n(ti18n, "NO_THEME_SELECTED_WARNING");
        }
        if (!template.savedThemeConfig.themeConfig.savedCaptionConfig) {
            return window.getI18n(ti18n, "NO_CAPTION_CONFIG_WARNING");
        }
        return null;
    };

    // Handle theme caption switch
    const handleThemeCaptionChange = (checked: boolean) => {        
        // Create a new template with updated override value
        const updatedTemplate = {
            ...template,
            overrideThemeCaption: checked
        };

        onTemplateChange(updatedTemplate)

    };

    const handleOverideCaptionThemeSwitchChange = (checked: boolean) => {        
        // Create a new template with updated override value
        const updatedTemplate = {
            ...template,
            overrideThemeCaption: checked
        };

        onTemplateChange(updatedTemplate)

    };

    const handleUseThemeCaptionThemeSwitchChange = (checked: boolean) => {        
        // Create a new template with updated override value
        const updatedTemplate = {
            ...template,
            overrideThemeCaption: !checked
        };

        onTemplateChange(updatedTemplate)

    };

    

    // Handle show caption switch
    const handleShowCaptionChange = (checked: boolean) => {
        onTemplateChange({
            ...template,
            showCaption: checked
        });
    };

    if (isEditing && editingCaption) {
        return (
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center">
                    <TrebbleButton 
                        type="text" 
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            // Revert to the original state if it exists
                            if (originalCaptionBeforeEdit !== null && originalOverrideThemeCaptionBeforeEdit !== null) {
                                onTemplateChange({
                                    ...template,
                                    savedCaptionConfig: originalCaptionBeforeEdit,
                                    overrideThemeCaption: originalOverrideThemeCaptionBeforeEdit
                                });
                            }
                            setIsEditing(false);
                            setEditingCaption(null);
                            // Clear original state
                            setOriginalCaptionBeforeEdit(null);
                            setOriginalOverrideThemeCaptionBeforeEdit(null);
                            // Restore scroll position after a brief delay to ensure DOM is updated
                            setTimeout(() => {
                                if (scrollContainerRef.current) {
                                    scrollContainerRef.current.scrollTop = scrollPosition;
                                }
                            }, 0);
                            onEditingChange(false);
                        }}
                    >
                        Back
                    </TrebbleButton>
                </div>

                {/* Edit Form */}
                <div className="flex-grow overflow-auto">
                    <CaptionEditForm 
                        caption={editingCaption}
                        themeConfig={template.savedThemeConfig.themeConfig}
                        onCaptionChange={(updatedCaption) => {
                            setEditingCaption(updatedCaption);
                            onTemplateChange({
                                ...template,
                                savedCaptionConfig: updatedCaption,
                                overrideThemeCaption: true // Keep override true while editing
                            });
                        }}
                    />
                </div>

                {/* Fixed Bottom Bar */}
                <div className="sticky bottom-0 bg-white border-t p-4 mt-4 dark:bg-neutral-800 dark:border-neutral-600">
                    <TrebbleButton type="primary" block onClick={handleSave}>
                        Save Caption
                    </TrebbleButton>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex flex-col h-full">
                {/* Fixed Header Skeleton */}
                <div className="sticky top-0 z-20 bg-white border-b dark:bg-neutral-800 dark:border-neutral-600">
                    <TrebbleCard size="small" className="border-0">
                        <div className="flex flex-col gap-2">
                            <TrebbleSkeleton.Input active style={{ width: 120, height: 24, marginBottom: 8, background: token.token.colorSplit }} />
                            <div className="flex items-center justify-between">
                                <TrebbleSkeleton.Input active style={{ width: 100, height: 20, background: token.token.colorSplit }} />
                                <TrebbleSkeleton.Button active size="small" style={{ width: 40, height: 20, background: token.token.colorSplit }} />
                            </div>
                            <div className="border-t border-b py-2 dark:border-neutral-600">
                                <TrebbleSkeleton.Input active style={{ width: 160, height: 32, background: token.token.colorSplit }} />
                            </div>
                        </div>
                    </TrebbleCard>
                </div>
                {/* Scrollable Content Skeleton */}
                <div className="flex-grow overflow-auto">
                    <div className="sticky top-0 z-10 bg-white dark:bg-neutral-800 dark:text-white mt-4 border-x border-t border-neutral-100 dark:border-neutral-600">
                        <TrebbleSkeleton.Input active style={{ width: 120, height: 32, margin: 12, background: token.token.colorSplit }} />
                    </div>
                    <TrebbleCard size="small">
                        <div className="grid p-2 mb-8">
                            {[...Array(2)].map((_, idx) => (
                                <TrebbleSkeleton
                                    key={idx}
                                    active
                                    style={{ width: 160, height: 80, borderRadius: 0 }}
                                    paragraph={true}
                                    title={{ width: '60%' }}
                                />
                            ))}
                        </div>
                    </TrebbleCard>
                </div>
                {/* Bottom Bar Skeleton */}
                <div className="sticky bottom-0 bg-white border-t p-4 mt-4 z-20 dark:bg-neutral-800 dark:border-neutral-600">
                    <div className="flex gap-2">
                        <TrebbleSkeleton.Button active block style={{ height: 32, borderRadius: 0, width: '100%', background: token.token.colorSplit }} />
                        <TrebbleSkeleton.Button active block style={{ height: 32, borderRadius: 0, width: '100%', background: token.token.colorSplit }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Fixed Header */}
            <div className="sticky top-0 z-20 bg-white border-b dark:bg-neutral-800 dark:border-neutral-600">
                {/* Create New Caption Button - Add this section */}
                <div className="p-4 border-b dark:border-neutral-600">
                    <TrebbleButton 
                        type="primary" 
                        block 
                        onClick={handleCreateNew}
                    >
                        {window.getI18n(ti18n, "CREATE_NEW_CAPTION_TITLE")}
                    </TrebbleButton>
                </div>
                <TrebbleCard size="small" className="border-0">
                    <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 hidden">{window.getI18n(ti18n, "CAPTION_SETTINGS_TITLE")}</span>
                        <div className="flex items-center justify-between">
                            <FieldContainer label={window.getI18n(ti18n, "SHOW_CAPTION_LABEL")} direction="row" className="mb-0">
                                <TrebbleSwitch
                                    size="small"
                                    checked={template.showCaption}
                                    onChange={handleShowCaptionChange}
                                />
                            </FieldContainer>
                        </div>
                        {template.showCaption && false && !template.overrideThemeCaption && (
                            <FieldContainer label={window.getI18n(ti18n, "OVERRIDE_THEME_CAPTION_LABEL")} direction="row" className=" ">
                                <TrebbleSwitch
                                    size="small"
                                    checked={template.overrideThemeCaption}
                                    onChange={handleOverideCaptionThemeSwitchChange}
                                />
                            </FieldContainer>
                        )}
                        {template.showCaption  && (
                            <FieldContainer label={window.getI18n(ti18n, "USE_THEME_CAPTION_LABEL")} direction="row" className="border-t dark:border-neutral-600">
                                <TrebbleSwitch
                                    size="small"
                                    checked={!template.overrideThemeCaption}
                                    onChange={handleUseThemeCaptionThemeSwitchChange}
                                />
                            </FieldContainer>
                        )}
                        {SHOW_SELECTED_CAPTION_PREVIEW && template.showCaption && (!SHOW_SELECTED_CAPTION_ONLY_FOR_SELECTED_THEME || !template.overrideThemeCaption) && selectedCaption && (
                            <div className="  py-2">
                                <FieldContainer label={template.overrideThemeCaption ? window.getI18n(ti18n, "SELECTED_CAPTION_PREVIEW") : window.getI18n(ti18n, "THEME_CAPTION_PREVIEW")} direction="col" className="mb-0">
                                    <CaptionTemplate
                                        caption={selectedCaption}
                                        brandConfig={template?.savedThemeConfig.themeConfig?.brandConfig}
                                        onClick={() => {}}
                                        selected={false}
                                    />
                                </FieldContainer>
                            </div>
                        )}
                    </div>
                </TrebbleCard>
            </div>

            {/* Scrollable Content */}
            <div ref={scrollContainerRef} className="flex-grow overflow-auto">
                {template.showCaption ? (
                    template.overrideThemeCaption ? (
                        <>
                            {/* Your Captions */}
                            <CaptionList
                                userCaptions={userCaptions}
                                trebbleCaptionsByCategory={trebbleCaptionsByCategory}
                                selectedCaption={selectedCaption}
                                selectedCaptionDetails={selectedCaptionDetails}
                                brandConfig={template.savedThemeConfig.themeConfig.brandConfig}
                                onCaptionSelect={handleCaptionSelect}
                                onEdit={handleEdit}
                                onDuplicate={handleDuplicate}
                                showActions={false}
                            />
                            {/* Fixed Bottom Bar - Only show when captions are displayed */}
                            <div className="sticky bottom-0 bg-white border-t p-4 mt-4 z-20 dark:bg-neutral-800 dark:border-neutral-600">
                                <div className="flex gap-2">
                                    <TrebbleButton 
                                        block 
                                        disabled={!selectedCaptionDetails || (selectedCaptionDetails?.type === 'trebble')}
                                        onClick={handleEdit}
                                    >
                                        {window.getI18n(ti18n, "EDIT_BUTTON")}
                                    </TrebbleButton>
                                    <TrebbleButton block type="primary" disabled={!selectedCaptionDetails} onClick={handleDuplicate}>
                                        {window.getI18n(ti18n, "DUPLICATE_BUTTON")}
                                    </TrebbleButton>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-full w-full ">
                            {getThemeCaptionMessage() || window.getI18n(ti18n, "USING_THEME_CAPTION")}
                        </div>
                    )
                ) : (
                    <div className="flex items-center justify-center h-full w-full ">
                        {window.getI18n(ti18n, "CAPTIONS_HIDDEN_MESSAGE")}
                    </div>
                )}
            </div>
        </div>
    );
};
